// Session AFH: the "Are you our engineer? Log this visit" flow on the public RFID service pages.
// A muted text button opens an inline modal (this page's own dark chrome, deliberately NOT the
// office ModalShell, which hard-codes office-ui). The engineer enters their email; we POST it to
// the public engineer-login endpoint, which emails a single-use sign-in link if the email belongs
// to an active engineer or office user on this lift's tenant. The endpoint is opaque by design, so
// ANY response (recognised or not) shows the same "check your email" confirmation.
//
// Shared by NfcServicePage and EquipmentServicePage so both behave identically, and since Session
// AMZ by the INTERNAL attendance tap page too. The `surface` prop picks which endpoint the token is
// sent to, and it is NOT cosmetic: an attendance tap token does not exist in the nfcToken column, so
// posting a tap token to the RFID endpoint is a guaranteed 404 and the engineer would never get a
// link. House rules: plain English, no double hyphens, the single client phone is not needed here.
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { api } from "../../lib/api";

export function EngineerLogVisit({ token, surface = "nfc" }: { token: string; surface?: "nfc" | "tap" }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const inputRef = useRef<HTMLInputElement>(null);

  function close() {
    setOpen(false);
    setEmail("");
    setStatus("idle");
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    inputRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await (surface === "tap" ? api.publicTap : api.publicNfc).engineerLogin(token, email.trim());
    } catch {
      // The endpoint is opaque by design and never leaks whether the email was recognised, so even
      // a genuine network error shows the same confirmation. Nothing sensitive is exposed; the user
      // can simply try again from the page.
    }
    setStatus("sent");
  }

  return (
    <>
      <div className="flex-none pt-1 text-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="min-h-[44px] font-body text-xs text-ele-body opacity-60 transition hover:opacity-90"
        >
          Are you our engineer? Log this visit
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          role="presentation"
          onClick={close}
        >
          <div
            className="w-full max-w-sm rounded-lg border border-ele-purple-accent bg-ele-bg p-5"
            role="dialog"
            aria-modal="true"
            aria-label="Log this visit"
            onClick={(e) => e.stopPropagation()}
          >
            {status === "sent" ? (
              <div className="text-center">
                <h2 className="mb-2 font-heading text-xl text-ele-white">Check your email</h2>
                <p className="text-sm text-ele-body">Check your email for a sign-in link.</p>
                <button
                  type="button"
                  onClick={close}
                  className="mt-5 min-h-[44px] w-full rounded-lg border border-white/10 bg-black/25 px-4 text-sm font-semibold text-ele-body transition hover:bg-white/5"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div className="mb-1 flex items-start justify-between gap-3">
                  <h2 className="font-heading text-xl text-ele-white">Log this visit</h2>
                  <button
                    type="button"
                    onClick={close}
                    aria-label="Close"
                    className="flex h-11 w-11 flex-none items-center justify-center text-ele-body opacity-60 transition hover:opacity-100"
                  >
                    &#10005;
                  </button>
                </div>
                <p className="mb-3 text-sm text-ele-body opacity-80">
                  Enter your email address and we will send you a sign-in link.
                </p>
                <input
                  ref={inputRef}
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@eastlondonelevators.co.uk"
                  autoComplete="email"
                  aria-label="Email address"
                  className="mb-4 min-h-[44px] w-full rounded-lg border border-white/15 bg-black/25 px-3 text-sm text-ele-white focus:border-ele-loler focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="flex min-h-[44px] w-full items-center justify-center rounded-lg bg-ele-green px-5 font-heading text-base font-bold uppercase tracking-wide text-ele-bg transition hover:brightness-110 disabled:opacity-60"
                >
                  {status === "sending" ? "Sending" : "Send link"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
